import { useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { AuthImage, fmtDT } from "./mpUtils";
import { PaperPlaneTilt } from "@phosphor-icons/react";

/** The per-request message thread (brief, quotes, status changes, chat) — same component on both sides. */
export function RequestThread({ request, mySide, onSend, actions }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await onSend(text.trim()); setText(""); } finally { setBusy(false); }
  };
  const closed = ["declined", "withdrawn"].includes(request.status);
  const hasActions = Array.isArray(actions?.props?.children) ? actions.props.children.some(Boolean) : Boolean(actions?.props?.children);
  return (
    <div className="rounded-lg border border-border bg-card flex flex-col" data-testid="request-thread">
      <div className="p-3 space-y-3 max-h-[52vh] overflow-y-auto">
        {(request.messages || []).map((m) => {
          const mine = m.side === mySide;
          const sys = m.kind === "status" || m.kind === "system";
          return (
            <div key={m.id} className={`flex ${sys ? "justify-center" : mine ? "justify-end" : "justify-start"}`} data-testid={`thread-msg-${m.kind}`}>
              {sys ? (
                <span className="text-[11px] text-muted-foreground bg-muted/60 rounded-full px-3 py-1">{m.by}: {m.text} · {fmtDT(m.at)}</span>
              ) : (
                <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : m.kind === "quote" ? "bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800" : "bg-muted"}`}>
                  {m.kind === "brief" && <p className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-0.5">Brief</p>}
                  {m.kind === "quote" && <p className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-0.5">Quote</p>}
                  <p className="whitespace-pre-wrap break-words">{m.text}</p>
                  <p className={`text-[10px] mt-1 ${mine ? "opacity-70" : "text-muted-foreground"}`}>{m.by} · {fmtDT(m.at)}</p>
                </div>
              )}
            </div>
          );
        })}
        {request.photos?.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1" data-testid="request-photos">
            {request.photos.map((p) => <AuthImage key={p.id} url={p.url} alt={p.name} className="h-20 w-20 rounded-md object-cover border border-border" />)}
          </div>
        )}
      </div>
      {hasActions && <div className="border-t border-border p-3 flex flex-wrap gap-2">{actions}</div>}
      {!closed && (
        <div className="border-t border-border p-3 flex gap-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Write a message…" className="flex-1 text-sm" data-testid="thread-message-input"
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }} />
          <Button onClick={send} disabled={busy || !text.trim()} data-testid="thread-send-btn"><PaperPlaneTilt size={16} /></Button>
        </div>
      )}
    </div>
  );
}
